// @ts-strict-ignore
import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import {
  Button,
  DataTableCellVariants,
  DataTableColumnAlignments,
  DataTableRowData,
  DropdownMenu,
  DropdownMenuItem,
  Highlight,
  Icon,
} from '@node-space/ui-components'
import { ellipseMiddleText } from '@node-space/utils'
import { DateFormat } from 'constants/General'
import { getTransactionTitle } from 'pages/WalletDetails/hooks/useTitleTransaction'
import { getIsRefundable } from 'pages/Wallets/components/Refunds/utils'
import { formatTotal } from 'pages/Wallets/components/Table/utils'
import { Transaction, TransactionStatus } from 'types/transaction'
import { TransactionType } from 'types/transaction/TransactionType'
import {
  getHighlightType,
  getSupportedPaymentTypes,
  getTransactionReference,
  statusMappings,
  titleCase,
} from 'utils/utils'

const getTransactionDate = (transaction: Transaction) => {
  switch (transaction?.type) {
    case TransactionType.USER_DEPOSIT:
      return transaction?.context?.depositDetails?.valueDate || transaction?.dateCreated
    case TransactionType.USER_WITHDRAWAL:
      return transaction?.context?.withdrawalDetails?.valueDate || transaction?.dateCreated
    default:
      return transaction?.dateCreated
  }
}

interface UseTransactionTableDataProps {
  transactions: Transaction[]
  openSidePanelSummary: (index: number) => void
  openRefundSheet?: (transaction: Transaction) => void
}

const useTransactionTableData = ({
  transactions,
  openSidePanelSummary,
  openRefundSheet,
}: UseTransactionTableDataProps) => {
  const { t } = useTranslation()
  const { enableFiatRefunds } = useFeatureFlags()

  const columns = useMemo(() => {
    return [
      { key: 'transactionType', label: t('transactionType'), variant: DataTableCellVariants.TEXT },
      { key: 'amount', label: t('amount'), variant: DataTableCellVariants.TEXT },
      { key: 'date', label: t('date'), variant: DataTableCellVariants.TEXT },
      {
        key: 'paymentsReference',
        label: t('payments.reference'),
        variant: DataTableCellVariants.TEXT,
      },
      {
        key: 'status',
        label: t('status'),
        variant: DataTableCellVariants.HIGHLIGHT,
      },
      {
        key: 'action',
        label: '',
        variant: DataTableCellVariants.ACTION,
        align: DataTableColumnAlignments.RIGHT,
      },
    ]
  }, [t])

  const tableData: DataTableRowData[] = transactions?.map((transaction, transactionIndex) => {
    const quoteStatus = transaction?.context?.quoteStatus
    const transactionType = transaction?.type
    const transactionSubtype = transaction?.context?.subType
    const transactionsTypeSubType = transactionSubtype
      ? `${transactionType}_${transactionSubtype}`
      : `${transactionType}_`

    const supportedPaymentTypes = getSupportedPaymentTypes()

    const isSettlingQuote =
      transactionsTypeSubType === supportedPaymentTypes.CONVERT_IN &&
      quoteStatus === TransactionStatus.PROCESSING

    const status = isSettlingQuote
      ? TransactionStatus.PROCESSING
      : transaction?.context?.status || statusMappings[transactionsTypeSubType]
    const mappedStatus = getHighlightType(status) ?? 'neutral'
    const title = getTransactionTitle({ transaction, t })
    const transactionRowId = `${transaction?.id}-${transaction?.hash}-${transaction?.dateCreated}`

    const isRefundable = getIsRefundable(transaction)

    return {
      id: transactionRowId,
      testid: transactionRowId,
      onRowClick: () => openSidePanelSummary(transactionIndex),
      cells: [
        {
          key: 'transactionType',
          variant: 'TEXT',
          Text: title,
        },
        {
          key: 'amount',
          variant: DataTableCellVariants.TEXT,
          Text: formatTotal(transaction?.amount, transaction.wallet?.currency?.code),
        },
        {
          key: 'date',
          variant: DataTableCellVariants.TEXT,
          Text: dayjs(getTransactionDate(transaction)).format(DateFormat.DD_MMM_YYYY),
        },
        {
          key: 'paymentsReference',
          variant: DataTableCellVariants.TEXT,
          Text: ellipseMiddleText(getTransactionReference(transaction), 20, 10),
        },
        {
          key: 'status',
          variant: DataTableCellVariants.HIGHLIGHT,
          ...(status && {
            Highlights: [
              <Highlight key={status} variant="status" type={mappedStatus}>
                {titleCase(status)}
              </Highlight>,
            ],
          }),
        },
        {
          key: 'action',
          variant: DataTableCellVariants.ACTION,
          Actions: [
            <DropdownMenu
              key={`context-menu-${transactionRowId}`}
              DropdownMenuItems={[
                <DropdownMenuItem
                  key={`${transactionRowId}-info`}
                  LeadingIcon={<Icon name="Info" />}
                  onClick={() => openSidePanelSummary(transactionIndex)}
                >
                  {t('wallets.details')}
                </DropdownMenuItem>,
                ...(enableFiatRefunds && isRefundable
                  ? [
                      <DropdownMenuItem
                        key={`refund-${transaction?.hash}`}
                        LeadingIcon={<Icon name="DocArrowReturnCustom" />}
                        onClick={() => openRefundSheet?.(transaction)}
                      >
                        {t('wallets.refund')}
                      </DropdownMenuItem>,
                    ]
                  : []),
              ]}
            >
              <Button iconElement={<Icon name="DotsThreeVerticalCustom" />} variant="ghost" />
            </DropdownMenu>,
          ],
        },
      ],
    }
  })

  return { tableData, columns }
}

export default useTransactionTableData
